import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box, Card, CardContent, Typography, Grid, CircularProgress, 
  Divider, Chip, Button, Paper
} from '@mui/material';
import { ArrowLeft, Clock, CheckCircle, XCircle, LogIn, Fingerprint, Calendar } from 'lucide-react';
import { attendanceApi } from '@/api/attendance.api';
import useSchoolStore from '@/store/schoolStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { usePageMeta } from '@/hooks/usePageMeta';

const StatusBadge = ({ status }) => {
  const getProps = () => {
    switch (status) {
      case 'PRESENT': return { color: 'success', label: 'Present' };
      case 'ABSENT': return { color: 'error', label: 'Absent' };
      case 'HALF_DAY': return { color: 'warning', label: 'Half Day' };
      case 'ON_LEAVE': return { color: 'info', label: 'On Leave' };
      case 'HOLIDAY': return { color: 'default', label: 'Holiday' };
      default: return { color: 'default', label: status || 'Unknown' };
    }
  };
  
  const props = getProps();
  return <Chip {...props} sx={{ fontWeight: 600, px: 1 }} />;
};

export function StaffAttendanceDetailsPage() {
  const { staffId } = useParams();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date');
  const navigate = useNavigate();
  
  usePageMeta('Staff Attendance Details | Attendance', [
    { label: 'Attendance', path: '/attendance' },
    { label: 'Daily Overview', path: '/attendance/daily' },
    { label: 'Staff Details' },
  ]);

  const { currentSchool } = useSchoolStore();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (staffId && date && currentSchool?.id) {
      fetchDetails();
    }
  }, [staffId, date, currentSchool]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await attendanceApi.getStaffAttendanceDate(currentSchool.id, staffId, date);
      if (res.success) {
        setData(res);
      } else {
        setError('Failed to load attendance details');
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred while fetching details');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    return new Date(timeStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Staff Attendance Details"
        subtitle={date ? new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''}
        action={
          <Button 
            variant="outlined"
            startIcon={<ArrowLeft size={18} />} 
            onClick={() => navigate(-1)}
          >
            Back to List
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" variant="h6">{error}</Typography>
            <Button sx={{ mt: 2 }} onClick={fetchDetails}>Try Again</Button>
          </CardContent>
        </Card>
      ) : data ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Main Staff Info Card */}
          <Card>
            <CardContent sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight="700" gutterBottom>{data.staff?.name}</Typography>
                <Typography variant="body1" color="text.secondary">
                  {data.staff?.employee_code} • {data.staff?.designation?.name} • {data.staff?.department?.name}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Overall Status</Typography>
                <StatusBadge status={data.attendance?.attendance_status} />
                {data.attendance?.is_late && (
                  <Chip label="Late" color="warning" variant="outlined" sx={{ ml: 1, fontWeight: 600 }} />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Metrics Grid */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>Check In</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>{formatTime(data.attendance?.check_in_time)}</Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.lighter', color: 'success.main' }}>
                    <CheckCircle size={24} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>Check Out</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>{formatTime(data.attendance?.check_out_time)}</Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'error.lighter', color: 'error.main' }}>
                    <XCircle size={24} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>Worked Hours</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
                      {data.attendance?.worked_hours ? `${data.attendance.worked_hours} hrs` : '--'}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.lighter', color: 'primary.main' }}>
                    <Clock size={24} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>Method</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
                      {data.attendance?.attendance_method || '--'}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.lighter', color: 'info.main' }}>
                    <Fingerprint size={24} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {data.attendance?.remarks && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>Remarks</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  {data.attendance.remarks}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
         <Card>
          <CardContent sx={{ p: 8, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Calendar size={48} color="#ccc" style={{ marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary">No attendance data available for this date.</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
